import React, {useEffect, useRef, useState} from "react";
import * as THREE from "three";
import {GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader.js";
import {OBJLoader} from "three/examples/jsm/loaders/OBJLoader.js";
import {FBXLoader} from "three/examples/jsm/loaders/FBXLoader.js";
import {OrbitControls} from "three/examples/jsm/controls/OrbitControls.js";
import { link } from "../../../config";

const Product3DViewer = ({modelUrl, encoded = false, format}) => {
	const mountRef = useRef();
	const controlsRef = useRef();
	const cameraRef = useRef();
	const sceneRef = useRef();
	const modelRef = useRef();
	const rendererRef = useRef();
	const animationIdRef = useRef();
	const prevMountStyleRef = useRef("");
	const prevCanvasStyleRef = useRef("");
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");
	const [autoRotate, setAutoRotate] = useState(true);
	const [hiDpi, setHiDpi] = useState(true);
	const [viewerHeight, setViewerHeight] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 400 : 520));

	const buttonStyle = {
		appearance: "none",
		background: "#ffffff",
		border: "1px solid #e6e6eb",
		borderRadius: 10,
		padding: "6px 10px",
		fontSize: 12,
		color: "#111827",
		cursor: "pointer",
		transition: "all 0.15s ease",
		lineHeight: 1.2,
	};

	// Init THREE scene once
	useEffect(() => {
		const mount = mountRef.current;
		if (!mount) return;
		const width = mount.clientWidth;
		const height = mount.clientHeight;

		const scene = new THREE.Scene();
		scene.background = new THREE.Color(0xf7f7f8);
		sceneRef.current = scene;

		const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
		camera.position.set(0, 1.6, 3.2);
		cameraRef.current = camera;

		const renderer = new THREE.WebGLRenderer({antialias: true, alpha: false});
		renderer.setSize(width, height);
		renderer.setPixelRatio(Math.min(window.devicePixelRatio, hiDpi ? 2 : 1));
		renderer.shadowMap.enabled = true;
		renderer.shadowMap.type = THREE.PCFSoftShadowMap;
		if (renderer.domElement.parentNode !== mount) {
			mount.appendChild(renderer.domElement);
		}
		renderer.outputColorSpace = THREE.SRGBColorSpace;
		renderer.toneMapping = THREE.ACESFilmicToneMapping;
		renderer.toneMappingExposure = 1.0;
		rendererRef.current = renderer;

		const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 1.0);
		hemiLight.position.set(0, 20, 0);
		scene.add(hemiLight);
		const dirLight1 = new THREE.DirectionalLight(0xffffff, 1.0);
		dirLight1.position.set(5, 10, 7.5);
		dirLight1.castShadow = true;
		dirLight1.shadow.mapSize.width = 1024;
		dirLight1.shadow.mapSize.height = 1024;
		scene.add(dirLight1);
		const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.6);
		dirLight2.position.set(-5, 3, -5);
		scene.add(dirLight2);

		const groundGeo = new THREE.PlaneGeometry(20, 20);
		const groundMat = new THREE.ShadowMaterial({opacity: 0.2});
		const ground = new THREE.Mesh(groundGeo, groundMat);
		ground.rotation.x = -Math.PI / 2;
		ground.position.y = -0.01;
		ground.receiveShadow = true;
		scene.add(ground);

		const controls = new OrbitControls(camera, renderer.domElement);
		controls.enableDamping = true;
		controls.dampingFactor = 0.08;
		controls.screenSpacePanning = false;
		controls.minDistance = 1;
		controls.maxDistance = 10;
		controls.maxPolarAngle = Math.PI / 2;
		controls.autoRotate = autoRotate;
		controls.autoRotateSpeed = 1.0;
		controlsRef.current = controls;

		const handleResize = () => {
			const newWidth = mount.clientWidth;
			const newHeight = mount.clientHeight;
			renderer.setSize(newWidth, newHeight);
			camera.aspect = Math.max(0.01, newWidth / Math.max(1, newHeight));
			camera.updateProjectionMatrix();
		};
		window.addEventListener("resize", handleResize);
		const handleFullscreenChange = () => {
			// Run resize now and after layout settles to avoid 0 sizes
			handleResize();
			setTimeout(handleResize, 0);
			setTimeout(handleResize, 100);
			if (controlsRef.current) controlsRef.current.enabled = true;
			// Restore styles when exiting fullscreen in case UA left inline dimensions
			if (!document.fullscreenElement) {
				const mountEl = mountRef.current;
				const renderer = rendererRef.current;
				if (mountEl) {
					if (prevMountStyleRef.current) {
						mountEl.style.cssText = prevMountStyleRef.current;
						prevMountStyleRef.current = "";
					} else {
						mountEl.style.removeProperty('position');
						mountEl.style.removeProperty('inset');
						mountEl.style.removeProperty('top');
						mountEl.style.removeProperty('left');
						mountEl.style.removeProperty('right');
						mountEl.style.removeProperty('bottom');
						mountEl.style.removeProperty('z-index');
					}
				}
				if (renderer && renderer.domElement) {
					const canvas = renderer.domElement;
					if (prevCanvasStyleRef.current) {
						canvas.style.cssText = prevCanvasStyleRef.current;
						prevCanvasStyleRef.current = "";
					} else {
						canvas.style.width = '100%';
						canvas.style.height = '100%';
						canvas.style.removeProperty('position');
						canvas.style.removeProperty('inset');
					}
					// Force reset to parent size and update canvas style
					renderer.setSize(mountEl.clientWidth, mountEl.clientHeight, true);
				}
			}
		};
		document.addEventListener('fullscreenchange', handleFullscreenChange);

		const raycaster = new THREE.Raycaster();
		const pointer = new THREE.Vector2();
		const domEl = renderer.domElement;
		domEl.style.cursor = 'grab';
		const onMouseDown = () => { domEl.style.cursor = 'grabbing'; };
		const onMouseUp = () => { domEl.style.cursor = 'grab'; };
		domEl.addEventListener('mousedown', onMouseDown);
		domEl.addEventListener('mouseup', onMouseUp);
		const handleDblClick = (event) => {
			if (!modelRef.current) return;
			const rect = domEl.getBoundingClientRect();
			const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
			const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
			pointer.set(x, y);
			raycaster.setFromCamera(pointer, camera);
			const intersects = raycaster.intersectObject(modelRef.current, true);
			if (intersects.length) {
				const point = intersects[0].point;
				const controls = controlsRef.current;
				const dir = new THREE.Vector3().subVectors(camera.position, controls.target).normalize();
				const distance = controls.getDistance ? controls.getDistance() : 2.5;
				controls.target.copy(point);
				camera.position.copy(point).addScaledVector(dir, distance);
				camera.updateProjectionMatrix();
				controls.update();
			}
		};
		domEl.addEventListener('dblclick', handleDblClick);

		// Handle potential WebGL context loss (can happen on fullscreen transitions)
		const handleContextLost = (e) => {
			try { e.preventDefault(); } catch {}
			if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
		};
		const handleContextRestored = () => {
			// Kick the loop again and ensure size is correct
			handleResize();
			setTimeout(handleResize, 0);
			animate();
		};
		domEl.addEventListener('webglcontextlost', handleContextLost, false);
		domEl.addEventListener('webglcontextrestored', handleContextRestored, false);

		const animate = () => {
			animationIdRef.current = requestAnimationFrame(animate);
			controls.update();
			renderer.render(scene, camera);
		};
		animate();

		return () => {
			window.removeEventListener("resize", handleResize);
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
			if (rendererRef.current && rendererRef.current.domElement) {
				const el = rendererRef.current.domElement;
				el.removeEventListener('dblclick', handleDblClick);
				el.removeEventListener('mousedown', onMouseDown);
				el.removeEventListener('mouseup', onMouseUp);
				el.removeEventListener('webglcontextlost', handleContextLost);
				el.removeEventListener('webglcontextrestored', handleContextRestored);
			}
			try {
				if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
				if (controlsRef.current) controlsRef.current.dispose();
				if (rendererRef.current) {
					rendererRef.current.dispose();
					rendererRef.current = null;
				}
			} catch (e) {}
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// React to autoRotate state without re-initializing
	useEffect(() => {
		if (controlsRef.current) {
			controlsRef.current.autoRotate = autoRotate;
		}
	}, [autoRotate]);

	// React to hiDpi changes
	useEffect(() => {
		if (rendererRef.current) {
			rendererRef.current.setPixelRatio(Math.min(window.devicePixelRatio, hiDpi ? 2 : 1));
		}
	}, [hiDpi]);

	// Load/replace model when URL changes
	useEffect(() => {
		if (!modelUrl) {
			setIsLoading(false);
			return;
		}
		setIsLoading(true);
		setError("");
		// Resolve URL: if encoded string provided, call backend proxy; else use raw URL
		const resolvedUrl = encoded ? `${link}product/proxy-3d?e=${encodeURIComponent(modelUrl)}` : modelUrl;
		const scene = sceneRef.current;
		// Remove previous model
		if (modelRef.current && scene) {
			try {
				scene.remove(modelRef.current);
				modelRef.current.traverse?.((node) => {
					if (node.isMesh) {
						node.geometry?.dispose?.();
						if (Array.isArray(node.material)) node.material.forEach(m => m.dispose?.());
						else node.material?.dispose?.();
					}
				});
			} catch {}
		}

		const ext = (format ? format : resolvedUrl.split('.').pop()).toLowerCase();
		let loader;
		if (ext === 'glb' || ext === 'gltf') loader = new GLTFLoader();
		else if (ext === 'obj') loader = new OBJLoader();
		else if (ext === 'fbx') loader = new FBXLoader();
		else {
			setError('Unsupported 3D format');
			setIsLoading(false);
			return;
		}

		loader.load(
			resolvedUrl,
			(object) => {
				const model = object.scene || object;
				modelRef.current = model;
				const box = new THREE.Box3().setFromObject(model);
				const size = new THREE.Vector3();
				box.getSize(size);
				const maxDim = Math.max(size.x, size.y, size.z) || 1;
				const scale = 2 / maxDim;
				model.scale.setScalar(scale);
				box.setFromObject(model);
				const center = box.getCenter(new THREE.Vector3());
				model.position.sub(center);
				model.traverse(node => {
					if (node.isMesh) {
						node.castShadow = true;
						node.receiveShadow = false;
					}
				});
				scene.add(model);
				setIsLoading(false);
				// Auto fit
				try {
					const camera = cameraRef.current;
					const controls = controlsRef.current;
					if (camera && controls) {
						const fitBox = new THREE.Box3().setFromObject(model);
						const fitSize = new THREE.Vector3();
						fitBox.getSize(fitSize);
						const fitCenter = fitBox.getCenter(new THREE.Vector3());
						const maxDim2 = Math.max(fitSize.x, fitSize.y, fitSize.z);
						const fitHeightDistance = maxDim2 / (2 * Math.atan((Math.PI * camera.fov) / 360));
						const fitWidthDistance = fitHeightDistance / camera.aspect;
						const distance = Math.max(fitHeightDistance, fitWidthDistance);
						const direction = new THREE.Vector3()
							.subVectors(camera.position, controls.target)
							.normalize()
							.multiplyScalar(distance);
						controls.target.copy(fitCenter);
						camera.position.copy(fitCenter).add(direction);
						camera.near = maxDim2 / 100;
						camera.far = maxDim2 * 100;
						camera.updateProjectionMatrix();
						controls.update();
					}
				} catch {}
			},
			undefined,
			(err) => {
				console.error('Error loading model:', err);
				setError('Failed to load 3D model');
				setIsLoading(false);
			}
		);
	}, [modelUrl, encoded, format]);

	// Toolbar actions
	const handleResetView = () => {
		const camera = cameraRef.current;
		const controls = controlsRef.current;
		if (!camera || !controls) return;
		camera.position.set(0, 1.6, 3.2);
		controls.target.set(0, 0, 0);
		controls.update();
	};

	const handleZoomToFit = () => {
		const model = modelRef.current;
		const camera = cameraRef.current;
		const controls = controlsRef.current;
		if (!model || !camera || !controls) return;
		const box = new THREE.Box3().setFromObject(model);
		const size = new THREE.Vector3();
		box.getSize(size);
		const center = box.getCenter(new THREE.Vector3());
		const maxDim = Math.max(size.x, size.y, size.z);
		const fitHeightDistance = maxDim / (2 * Math.atan((Math.PI * camera.fov) / 360));
		const fitWidthDistance = fitHeightDistance / camera.aspect;
		const distance = Math.max(fitHeightDistance, fitWidthDistance);
		const direction = new THREE.Vector3()
			.subVectors(camera.position, controls.target)
			.normalize()
			.multiplyScalar(distance);
		controls.target.copy(center);
		camera.position.copy(center).add(direction);
		camera.near = maxDim / 100;
		camera.far = maxDim * 100;
		camera.updateProjectionMatrix();
		controls.update();
	};

	const handleToggleAutoRotate = () => {
		setAutoRotate(prev => !prev);
	};

	const handleFullscreen = () => {
		const elem = mountRef.current;
		if (!elem) return;
		if (document.fullscreenElement) {
			document.exitFullscreen();
		} else {
			// Save current inline styles to restore after exit
			const renderer = rendererRef.current;
			if (!prevMountStyleRef.current) prevMountStyleRef.current = elem.style.cssText || "";
			if (renderer && renderer.domElement && !prevCanvasStyleRef.current) {
				prevCanvasStyleRef.current = renderer.domElement.style.cssText || "";
			}
			elem.requestFullscreen?.();
		}
	};

	// Responsive height state
	useEffect(() => {
		const onResize = () => {
			setViewerHeight(window.innerWidth < 768 ? 400 : 520);
		};
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	return (
		<div style={{position: "relative", width: "100%"}}>
			<div
				ref={mountRef}
				style={{
					width: "100%",
					height: `${viewerHeight}px`,
					border: "1px solid #e6e6eb",
					borderRadius: "14px",
					overflow: "hidden",
					background: "linear-gradient(180deg,#fafafe 0%, #f3f4f7 100%)",
					boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
				}}
			>
				{!modelUrl && (
					<p style={{textAlign: "center", padding: 16}}>No 3D model available</p>
				)}
				{isLoading && modelUrl && (
					<div
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							background: "rgba(255,255,255,0.6)",
							backdropFilter: "blur(2px)",
						}}
					>
						<span style={{
							padding: "8px 12px",
							borderRadius: 8,
							background: "#fff",
							border: "1px solid #e6e6eb",
							color: "#555",
							fontSize: 14,
						}}>Loading 3D model…</span>
					</div>
				)}
				{error && (
					<div style={{position: "absolute", bottom: 8, left: 8, right: 8}}>
						<div style={{
							padding: "10px 12px",
							borderRadius: 10,
							background: "#fff2f2",
							border: "1px solid #ffd6d6",
							color: "#a10000",
							fontSize: 13,
						}}>
							{error}
						</div>
					</div>
				)}
			</div>

			{/* Toolbar - centered bottom overlay */}
			{modelUrl && (
				<div
					style={{
						position: "absolute",
						bottom: 50,
						left: "50%",
						transform: "translateX(-50%)",
						display: "flex",
						gap: 10,
						flexWrap: "wrap",
						alignItems: "center",
						justifyContent: "center",
						background: "rgba(255,255,255,0.92)",
						border: "1px solid #e6e6eb",
						borderRadius: 14,
						padding: 8,
						boxShadow: "0 10px 28px rgba(0,0,0,0.10)",
						backdropFilter: "saturate(1.1) blur(6px)",
						zIndex: 2,
					}}
				>
					<button onClick={handleResetView} style={buttonStyle}>Reset</button>
					<button onClick={handleZoomToFit} style={buttonStyle}>Fit</button>
					<button onClick={handleToggleAutoRotate} style={buttonStyle}>{autoRotate ? "Pause" : "Rotate"}</button>
					<button onClick={handleFullscreen} style={buttonStyle}>Full</button>
					<a href={encoded ? `${link}product/proxy-3d?e=${encodeURIComponent(modelUrl)}` : modelUrl} download style={{...buttonStyle, textDecoration: 'none'}}>Download</a>
				</div>
			)}
		</div>
	);
};

export default Product3DViewer;
